import {
	AdSuperGridListQuery,
	AdSuperGridListQueryVariables,
	CampaignSuperGridListQuery,
	CampaignSuperGridListQueryVariables,
	ClientSuperGridListQuery,
	ClientSuperGridListQueryItems,
	ClientSuperGridListQueryVariables, CreateGroupTargetingDevicesOs, CreateGroupTargetingDeviceType,
	GroupSuperGridListQuery,
	GroupSuperGridListQueryVariables,
	GroupTargetingDevicesOs,
} from 'wb-reddit/graphql/generated';
import { VARIABLES } from 'wb-reddit/styles/variables';
import {
	ElasticProperty, getLevelDepth, SuperGridValidationEntity,
} from '@aleph/supergrid';
import { createContext, Dispatch, SetStateAction } from 'react';
import { isString } from 'lodash';
import MoveIcon from 'wb-reddit/assets/move.svg';
import NotAllowedIcon from 'wb-reddit/assets/not-allowed.svg';
import { ICellRendererParams, GridApi } from 'ag-grid-community';
import { translate } from '@aleph/shared-components';

export enum RedditColors {
	AD_ACCOUNT = 'hsl(var(--blue-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	adAccount = 'hsl(var(--blue-500))',
	CAMPAIGN = 'hsl(var(--campaign-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	campaign = 'hsl(var(--campaign-500))',
	GROUP = 'hsl(var(--group-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	group = 'hsl(var(--group-500))',
	AD = 'hsl(var(--ad-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	ad = 'hsl(var(--ad-500))',
}

export enum RedditLevel {
	AD_ACCOUNT = 'adAccount',
	CAMPAIGN = 'campaign',
	GROUP = 'group',
	AD = 'ad',
	BREAKDOWN = 'breakdown',
}

export const RedditLevelId = {
	[RedditLevel.AD_ACCOUNT]: 'clientId',
	[RedditLevel.CAMPAIGN]:   'campaignId',
	[RedditLevel.GROUP]:      'groupId',
	[RedditLevel.AD]:         'adId',
};

export type RedditColDef = [
	{
		level: RedditLevel.AD_ACCOUNT
		data: ClientSuperGridListQuery
		variables: ClientSuperGridListQueryVariables
	},
	{
		level: RedditLevel.CAMPAIGN
		data: CampaignSuperGridListQuery
		variables: CampaignSuperGridListQueryVariables
	},
	{
		level: RedditLevel.GROUP
		data: GroupSuperGridListQuery
		variables: GroupSuperGridListQueryVariables
	},
	{
		level: RedditLevel.AD
		data: AdSuperGridListQuery
		variables: AdSuperGridListQueryVariables
	},
	{
		level: RedditLevel.BREAKDOWN
		data: any
		variables: any
	},
];

export enum RedditBreakdowns {
	ENTITIES = 'structure',
	DAILY = 'daily',
	INTEREST = 'interest',
	COMMUNITY = 'community',
	COUNTRY = 'country',
	REGION = 'region',
}

export enum NotificationReadStatus {
	Unread = 'Unread',
	Read = 'Read',
}

export const MAPPED_COLUMN_NAMES = {
	campaignName:                            'campaignName',
	groupName:                               'groupName',
	adName:                                  'adName',
	groupTargetingGeolocations:              'locations',
	groupTargetingCommunities:               'communities',
	groupTargetingExcludedCommunities:       'communitiesExcluded',
	groupTargetingExcludedGeolocations:      'locationsExcluded',
	groupTargetingInterests:                 'interests',
	groupTargetingKeywords:                  'keywords',
	groupTargetingExcludedKeywords:          'keywordsExcluded',
	groupTargetingCustomAudienceIds:         'customAudiences',
	groupTargetingExcludedCustomAudienceIds: 'customAudiencesExcluded',
	groupTargetingDevices:                   'devices',
	groupTargetingDevicesType:               'devices',
};

export type GraphQLErrorWithFormValidation = { debugMessage?: string, message?: string, hash?: string, form?: Record<string, { message?: string }> };

export const mapValidationErrors = (errors: GraphQLErrorWithFormValidation[], level?: RedditLevel) => errors.reduce<SuperGridValidationEntity[]>(
	(accu, error) => {
		Object.keys(error?.form ?? {}).forEach((field) => {
			accu.push({
				id:      error.hash ?? 'hash',
				field:   MAPPED_COLUMN_NAMES[field] ?? field,
				message: translate(error?.form?.[field]?.message || ''),
				level,
			});
		});

		return accu;
	},
	[],
);

const getGroupTargetingDevicesLabelMap = (devices: { value: string, label: string, make: string }[]) => {
	const devicesByMake = devices.reduce((accu, device) => {
		accu[device.make] = [...(accu[device.make] ?? []), device.value];

		return accu;
	}, {});

	return Object.keys(devicesByMake).map((make) => ({
		key:   make,
		value: devicesByMake[make],
	}));
};

export const mapDevicesToTargeting = ({ devices, deviceType, groupTargetingDevicesOSMinVersion }: {
	devices?: { value: string, label: string, make: string }[],
	deviceType?: { value: GroupTargetingDevicesOs | 'DESKTOP' }[],
	groupTargetingDevicesOSMinVersion?: string,
}) => {
	if (!devices?.length) return null;

	return [
		...(
			deviceType?.find?.(type => type.value === 'DESKTOP') ? [{
				groupTargetingDevicesType: CreateGroupTargetingDeviceType.Desktop,
			}] : []
		),
		...(
			(deviceType?.find?.(type => type.value === GroupTargetingDevicesOs.Android) || !(deviceType ?? []).length) ? [{
				groupTargetingDevicesType:         CreateGroupTargetingDeviceType.Mobile,
				groupTargetingDevicesOS:           CreateGroupTargetingDevicesOs.Android,
				groupTargetingDevicesOSMinVersion: isString(groupTargetingDevicesOSMinVersion)
					? groupTargetingDevicesOSMinVersion : undefined,
				groupTargetingDevicesLabelMap: getGroupTargetingDevicesLabelMap(
					devices?.filter(device => device.make !== 'Apple') ?? [],
				),
			}] : []
		),
		...(
			(deviceType?.find?.(type => type.value === GroupTargetingDevicesOs.Ios) || !(deviceType ?? []).length) ? [{
				groupTargetingDevicesType:         CreateGroupTargetingDeviceType.Mobile,
				groupTargetingDevicesOS:           CreateGroupTargetingDevicesOs.Ios,
				groupTargetingDevicesOSMinVersion: isString(groupTargetingDevicesOSMinVersion)
					? groupTargetingDevicesOSMinVersion : undefined,
				groupTargetingDevicesLabelMap: getGroupTargetingDevicesLabelMap(
					devices?.filter(device => device.make !== 'Apple') ?? [],
				),
			}] : []
		),
	];
};

export const REDDIT_ALWAYS_VISIBLE_COLUMNS = [
	'index',
	'breakdown',
	'entityName',
	'expand',
	'status',
	'adAccountName',
	'campaignName',
	'groupName',
	'adName',
	'breakdownName',
	'clientPinnedSettings',
	'settingsElastic',
];

export enum GenericStatus {
	PendingPaused = 'PENDING_PAUSED',
	Paused = 'PAUSED',
	PendingActive = 'PENDING_ACTIVE',
	Active = 'ACTIVE',
	PendingWarning = 'PENDING_WARNING',
	Warning = 'WARNING',
	PendingError = 'PENDING_ERROR',
	Error = 'ERROR',
	Archived = 'ARCHIVED',
	PendingArchived = 'PENDING_ARCHIVED',
	Deleted = 'DELETED',
	PendingDeleted = 'PENDING_DELETED',
}

export const COLOR_PALETTE = {
	[GenericStatus.PendingPaused]: {
		backgroundColor: '#ABBAD4',
		dotColor:        '#ABBAD4',
	},
	[GenericStatus.Paused]: {
		backgroundColor: '#ABBAD4',
		dotColor:        VARIABLES.COLORS.GRAY[0],
	},
	[GenericStatus.PendingError]: {
		backgroundColor: VARIABLES.COLORS.GRAY[300],
		dotColor:        VARIABLES.COLORS.RED[500],
	},
	[GenericStatus.Error]: {
		backgroundColor: VARIABLES.COLORS.RED[500],
		dotColor:        VARIABLES.COLORS.GRAY[0],
	},
	[GenericStatus.PendingWarning]: {
		backgroundColor: VARIABLES.COLORS.GRAY[300],
		dotColor:        VARIABLES.COLORS.YELLOW[500],
	},
	[GenericStatus.Warning]: {
		backgroundColor: VARIABLES.COLORS.YELLOW[500],
		dotColor:        VARIABLES.COLORS.GRAY[0],
	},
	[GenericStatus.PendingActive]: {
		backgroundColor: '#ABBAD4',
		dotColor:        VARIABLES.COLORS.GREEN[500],
	},
	[GenericStatus.Active]: {
		backgroundColor: VARIABLES.COLORS.GREEN[500],
		dotColor:        VARIABLES.COLORS.GRAY[0],
	},
	[GenericStatus.PendingArchived]: {
		backgroundColor: VARIABLES.COLORS.GRAY[300],
		dotColor:        VARIABLES.COLORS.GRAY[300],
	},
	[GenericStatus.Archived]: {
		backgroundColor: VARIABLES.COLORS.GRAY[300],
		dotColor:        VARIABLES.COLORS.GRAY[0],
	},
	[GenericStatus.PendingDeleted]: {
		backgroundColor: VARIABLES.COLORS.GRAY[200],
		dotColor:        VARIABLES.COLORS.GRAY[200],
	},
	[GenericStatus.Deleted]: {
		backgroundColor: VARIABLES.COLORS.GRAY[200],
		dotColor:        VARIABLES.COLORS.GRAY[0],
	},
};

export const REDDIT_SUPERGRID_ICONS = {
	columnMovePin:   `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveMove:  `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveLeft:  `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveRight: `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveGroup: `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveValue: `<img src="${MoveIcon}" alt="move" width="16"/>`,
	dropNotAllowed:  `<img src=${NotAllowedIcon} alt="not-allowed" width="16"/>`,
};

export const showXWithoutFocus = (params: ICellRendererParams, elasticProperty: ElasticProperty<RedditLevel>) => {
	const gridTopLevel = getLevelDepth(params.context.config, params.context?.gridTopLevel);
	const elasticPropertyLevels = elasticProperty?.levels
		.map(level => getLevelDepth(params.context.config, level) - gridTopLevel);
	const focusedRowNode = params.api?.getRenderedNodes()
		.find(node => node.rowIndex === params.api?.getFocusedCell()?.rowIndex);
	const isLevelOpenedWith = elasticProperty?.lastClicked?.[params.data?.level] === params.rowIndex;
	const ensureLowestLevel = elasticPropertyLevels && Math.max(...elasticPropertyLevels)
		=== getLevelDepth(params.context.config, params.data?.level) - gridTopLevel;
	const areEditingLevelsBeingFocused = elasticPropertyLevels?.includes(focusedRowNode?.level ?? 0);

	return isLevelOpenedWith && ensureLowestLevel && !areEditingLevelsBeingFocused;
};

export const RedditSuperGridContext = createContext<{
	gridApi?: GridApi | null,
	setGridApi: Dispatch<SetStateAction<GridApi | null | undefined>>
}>({
	gridApi:    null,
	setGridApi: () => null,
});

export const processValueToJSON = (params: { value: any }) => {
	try {
		return JSON.stringify(params.value);
	} catch (e) {
		return params.value;
	}
};

export const processValueFromJSON = (params: { value: any }) => {
	try {
		return JSON.parse(params.value);
	} catch (e) {
		return params.value;
	}
};

export function isPendingAdAccount(account?: Partial<ClientSuperGridListQueryItems>) {
	return !account?.agencyAccount?.agencyAccountActivatedAt;
}

export function getClientRecursively(mutation: any): ClientSuperGridListQueryItems | undefined {
	if (mutation?.__typename === 'Client') {
		return mutation;
	}

	if (!mutation) {
		return undefined;
	}

	return getClientRecursively(mutation.parent);
}

export function getFundingInstrumentOptions(data: any) {
	return getClientRecursively(data)
		?.adAccount?.fundingInstruments
		?.map(fi => ({ label: fi.fundingInstrumentName, value: fi.fundingInstrumentId })) ?? [];
}
